// Shared VAM types + enums — the LEAF half of what used to be one 799-line VAMv2.ts.
//
// WHY THIS FILE EXISTS. VAMv2.ts is two unrelated things glued together: a re-export
// barrel for ~120 EvBooking/ProjectWizard WIDGETS, and ~680 lines of shared types/enums.
// Importing a type therefore dragged in every one of those widgets — and 55 of VAMv2's 86
// importers want ONLY the types. One of them (EvBooking/EvCast/EvCastList) is extended by
// MOZ/MOZ_EvCastList, which VAMv2 imports, closing a 3-module cycle through a BASE class:
//
//   MOZ_EvCastList --extends--> EvCastList --> VAMv2 --> MOZ_EvCastList
//
// `class X extends compose(Base)` runs at MODULE-EVAL, so a cycle through a base edge is
// an ESM TDZ crash-at-load waiting to happen — `npm run cycles` reports it as HARMFUL.
// (It predates the migration: the AMD loader tolerated cycles, ESM does not.) Splitting
// the types out breaks it at the root: this module imports no widget, so nothing can cycle
// back through it.
//
// VAMv2.ts RE-EXPORTS everything below, so all existing `from "…/VAMv2"` imports keep
// working untouched. NEW consumers that need only types should import from HERE — that
// keeps ~120 widget modules out of their dependency graph.
//
// The three imports below MUST stay `import type`: they are used only in type positions
// (`extends MainCatalogTypes.X`, `Array<OMtypes.Y>`), and `import type` is erased at
// compile time. A value import would give this module a runtime edge — ./OM imports VAMv2
// — and re-create exactly the cycle this split removes.
//
// NOT hand-maintained: Typescript/ is reproducible codemod output. This file is
// materialized by handfixes.py's NEWFILES step from codemod/newfiles/ — edit it THERE.

import type { MainCatalogTypes } from "./MainCatalogTypes";
import type { OMtypes } from "./OM";
import type { FileUploadTypes } from "../../moreCore/UI/Filetransfer/FileUpload/FileUploadTypes";

export enum VAMclassNames {
    "ev_projects" = "ev_projects",
    "ev_booking" = "ev_booking",
    "ev_task_descript" = "ev_task_descript",
    "ev_task_single" = "ev_task_single",
    "ev_task_type_A" = "ev_task_type_A",
    "vt_ev_task" = "vt_ev_task",
    "ev_cast" = "ev_cast",
    "performers" = "performers",
    "ev_program" = "ev_program",
    "opus" = "opus",
    "ev_catagories_1" = "ev_catagories_1",
    "ht_evbooking_evcategorie1" = "ht_evbooking_evcategorie1",
    "file_ev_project" = "file_ev_project",
    "service_ev_serv" = "service_ev_serv",
    "service_pack_ev_serv" = "service_pack_ev_serv",
    "pos_ev_serv" = "pos_ev_serv",
    "vt_travel" = "vt_travel",
    "resource_booking_1" = "resource_booking_1",
    "physical_resource" = "physical_resource",
    "physical_resources" = "physical_resources",
    "organisations_external" = "organisations_external",
    "organisations_internal" = "organisations_internal",
    "ev_distribution_channel" = "ev_distribution_channel",
    "ht_evbooking_evdistribution" = "ht_evbooking_evdistribution"

}


export enum VAMselectEntityDialogModes {
    evRoom = "selectEvRoom",
    evRoomAll = "selectEvRoomAll",
    evProject = "selectEvProject",
    evCategory = "selectEvCategory",
    evCast = "selectEvCast",
    evProgram = "selectEvProgram"
}


/* diverse wiederkehrende Typisierungen */
export namespace VAM2types {
    export type CommdocTypes =
        "project_offer" | "project_offer_E" |
        "project_invoice" | "project_invoice_E" |
        "project_closinginvoice" | "project_closinginvoice_E" |
        "project_invoice_credit" | "project_invoice_credit_E" |
        "project_invoice_cancel_fee" | "project_invoice_cancel_fee_E" |
        "project_prepayment" | "project_prepayment_E" |
        "project_individual_order" | "project_individual_order_E" |
        "project_individual_order_option" | "project_individual_order_option_E" |
        "project_planning_ev_serv" | "project_planning_ev_serv_E" |
        "project_service_contract" | "project_service_contract_E";

    export enum EvServiceType {
        "default" = 1,
        "yesno" = 2,
        "text" = 4
    }

    export enum EvBookingSyncMode {
        individual = 0,
        synced = 1,
        //empty = 100
    }
    export interface EvRoomEntity extends MainCatalogTypes.EvRoomEntity_Short {
        /* im Prinzip sollte es das gleiche sein wie bei MainCatalogs, aber ich kann es hier nicht unter Alias rausgeben */
    }

    export interface ArtisticPeriod extends PeriodEntity_Base {
        ref_financial_period?: PeriodEntity_Base;
    }
    export interface PeriodEntity_Base extends more.EntityAPIResult {
        description: string;
        is_active?: boolean;
    }
    export interface EvCategory extends more.EntityAPIResult {
        code: string;
        description: string;
        is_active: boolean;
        note: string;
        order: string;
        coloration: more.EntityAPIColor;
        gDisp_1: string;
    }
    export interface Genre extends more.EntityAPIResult {
        code: string;
        description: string;
        is_active: boolean;
        note: string;
        order: string;
        coloration: more.EntityAPIColor;
        gDisp_1: string;
    }
    export interface GenreEntry extends more.EntityAPIResultExtended {
        genreID: string,
        ref_ev_genre: Genre
    }
    export interface EvCategoryInBooking extends EvCategory {
        categoryID: string;
    }

    export interface EvService extends more.EntityAPIResult {
        order: number;
        orderCategory?: number;
        service_type: number;
        $service_type: string;
        description: string;
        description_D?: string;
        description_E?: string;
        quantity: number;
        quantity_multiplier: number;
        unitCode: string;
        EL_ht_service_ev_serv_ev_task_descript_service?: Array<more.EntityAPIResult>;
        EL_vt_services_main_service?: Array<more.EntityAPIResult>;
        ref_ev_task_descript?: EvTaskDescript;
        createEvTaskBeyond?: boolean;
        childService?: EvService;
    }
    export interface EvServicePackEntry_Aliased extends more.EntityAPIResult { // Webtool_EvBooking__GetEvServicePackagesAttributeNames
        servicePackAssignmentID: string;
        order: number;
        orderCategory?: number;
        service_type: number;
        $service_type: string;
        description: string;
        descriptionOverride?: string; // siehe BOB Webtool_EvBooking__GetEvServicePackagesAttributeNames
        description_D?: string;
        description_E?: string;
        quantity: number;
        quantity_multiplier: number;
        unitCode: string;
        is_option: boolean;
        discount: number;
    }
    export interface EvServicePack extends more.EntityAPIResult {
        description: string;
        service: Array<EvServicePackEntry_Aliased>
    }
    export interface EvServiceBooking {
        ID: string;
        Classname: string; //VAMclassNames.pos_ev_serv, // geht das?
        order: number;
        orderService?: number;
        orderCategory?: number;
        serviceID: string;
        //service_txt: string;
        ev_service_text_internal: string;
        service_original_description: string;
        description: string;
        $service_type: string;
        service_type: EvServiceType;
        service_type_text: number;
        is_cancelled: boolean;
        is_option: boolean;
        is_finished: boolean;
        quantity: number;
        quantity_multiplier: number;
        quantity_text: string;
        unitCode: string;
        is_yes_no_questionmark_1: "Y" | "N" | "?";
        is_oversteered_price: boolean;
        is_price_total_pricelist: boolean;
        price_unit_pricelist: number;
        price_total_pricelist: number;
        value_agiodisagio_unit: number;
        value_agiodisagio_total: number;
        discount: number;
        price_unit_change: number;
        value_discount_unit: number;
        value_discount_total: number;
        price_total_after_disc: number;
        price_unit_after_disc: number;
        VAT_value: number;
        price_total_incl_VAT: number;
        ref_pricelist: {
            ID: string;
            price_net: number;
        }
        evTasks: Array<{ ID: string }>;
        documents: Array<any>;
        EL_resource_booking_1_vt_pos_ev_serv: Array<ResourceBooking>;
        createEvTaskBeyond?: boolean;
        childService?: EvService;
    }

    export interface ResourceBooking extends Partial<more.EntityAPIResultExtended> {
        quantity: number; // Auslastung Ressource
        quantity_resource_max: number; // Maximalbestand der Ressource
        quantity_max: number; // Maximalauslastung
        quantity_pos_ev_serv?: number; // Menge laut LB
        quantity_rest?: number; // 
        ref_vt_resource: PhysicalResource; // 
        is_oversteered_quantity?: boolean; // 
        warning_level_res_booking?: ResourceWarningLevel; // 
        $warning_level_res_booking?: string;
        warning_quantity_level1?: number;
        warning_quantity_level2?: number;
        ref_ht_service_resource: ResourceAssignmentToService

    }

    export interface ResourceLoadCheckResultData {
        serviceID: string,
        serviceBookingID: string,
        resources: Array<ResourceLoadData>
    }

    export interface ResourceLoadData {
        resourceID: string,
        resourceBookingID: string,
        description: string,
        code: string,
        note: string,
        serviceMultiplier: number,
        amountPerService: number,
        amountNeeded: number,
        amountStock: number,
        amountWarning1: number,
        amountWarning2: number,
        amountRemaining: number,
        load: number,
        isVirtual: boolean,
        isWarningLevel1: boolean,
        isWarningLevel2: boolean,
    //    virtualAmountNeeded: number,
    //    virtualLoad: number
    }

    enum ResourceWarningLevel {
        "exceeded" = 3,
        "full" = 2,
        "available" = 1
    }

    interface ResourceAssignmentToService extends more.EntityAPIResult {
         quantity: number
         note: string
    }

    export interface PhysicalResource extends more.EntityAPIResultExtended  {
        code: string;
        coloration: more.EntityAPIColor,
        description: string;
        note: string;
        is_active: boolean;
        order: number; // 
        quantity: number; // vorhandene Menge Ressource
        warning_percentage_level1: number;
        warning_percentage_level2: number;
        warning_quantity_level1: number;
        warning_quantity_level2: number;
    }

    export interface EvTaskDescript {
        ID: string;
        is_active: string;
        description: string;
        description_D: string;
        description_E: string;
        code: string;
        order: number;
        qty_needed: number;
    }

    export interface EvTaskSingle {
        ID: string;
        ref_associate: Object;
    }

    export interface Performer extends more.EntityAPIResult {
        gDisp_1?: string;
        ref_address: {
            ID: string;
            pseudonym: string;
            gDisp_1: string;
        }
        ref_ev_profession: EvProfession;
    }

    export interface EvProfession extends more.EntityAPIResult {
        description: string;    
    }

    export interface EvCast extends more.EntityAPIResult {
        $Displayname: string,
        c_cast_address: string,
        c_cast_perfomer: string,
        order: number,
        ref_performer?: Performer
    }

    export interface EvCastEvProject extends EvCast, _synced {
        ref_performer_contract?: Entity_gDisp;
    }

    export interface PostalCode extends more.EntityAPIResult {
        postal_code: string,
        gDisp_1: string,
        city: string,
        ref_country: Country
    }
    export interface Country extends more.EntityAPIResult {
        description: string,
    }
   
    export interface Commdoc extends more.EntityAPIResult {
        ID: string,
        $ClassPrintname: string,
        doc_no: number,
        doc_date: Date,
        ref_doc_status: DocStatus,
        vt_files: Array<VtFiles>,
        is_notRelevant: boolean
        is_archived: boolean
    }
    export interface DocStatus {
        ID: string,
        description: string,
        is_finished: boolean,
        code?:string,
    }
    export interface VtFiles extends Partial<more.EntityAPIResult> {
        ID: string,
        filename: string
        ref_entity_1?: more.EntityAPIResultExtended;
    }
    export interface FileWithCategory extends VtFiles {
        ref_file_category: {
            ID: string;
            description: string;
        }
        guid?: string
    }

    export interface FileUploadWithCategories extends FileUploadTypes.UploadUnit {
        fileCategories: Array<{
            fileName: string,
            category: string,
            description?: string
        }>
    }
    export interface VoiceAttributes {
        instrumentation_group1_number_str: string,
        instrumentation_group2_number_str: string,
        instrumentation_group3_number_str: string,
        instrumentation_group4_number_str: string,
        instrumentation_group5_number_str: string,
        instrumentation_group6_number_str: string,
        instrumentation_group7_number_str: string,
        instrumentation_group8_number_str: string,
        instrumentation_group9_number_str: string,
        instrumentation_group10_number_str: string,
        instrumentation_group11_number_str: string,
        instrumentation_group12_number_str: string,
        instrumentation_group13_number_str: string,
        instrumentation_group14_number_str: string,
        instrumentation_group15_number_str: string,
        instrumentation_group16_number_str: string,
        instrumentation_group17_number_str: string,
        instrumentation_group18_number_str: string,
        instrumentation_group1_number: number,
        instrumentation_group2_number: number,
        instrumentation_group3_number: number,
        instrumentation_group4_number: number,
        instrumentation_group5_number: number,
        instrumentation_group6_number: number,
        instrumentation_group7_number: number,
        instrumentation_group8_number: number,
        instrumentation_group9_number: number,
        instrumentation_group10_number: number,
        instrumentation_group11_number: number,
        instrumentation_group12_number: number,
        instrumentation_group13_number: number,
        instrumentation_group14_number: number,
        instrumentation_group15_number: number,
        instrumentation_group16_number: number,
        instrumentation_group17_number: number,
        instrumentation_group18_number: number,
        is_oversteered_instrumentation_group1_number: boolean,
        is_oversteered_instrumentation_group2_number: boolean,
        is_oversteered_instrumentation_group3_number: boolean,
        is_oversteered_instrumentation_group4_number: boolean,
        is_oversteered_instrumentation_group5_number: boolean,
        is_oversteered_instrumentation_group6_number: boolean,
        is_oversteered_instrumentation_group7_number: boolean,
        is_oversteered_instrumentation_group8_number: boolean,
        is_oversteered_instrumentation_group9_number: boolean,
        is_oversteered_instrumentation_group10_number: boolean,
        is_oversteered_instrumentation_group11_number: boolean,
        is_oversteered_instrumentation_group12_number: boolean,
        is_oversteered_instrumentation_group13_number: boolean,
        is_oversteered_instrumentation_group14_number: boolean,
        is_oversteered_instrumentation_group15_number: boolean,
        is_oversteered_instrumentation_group16_number: boolean,
        is_oversteered_instrumentation_group17_number: boolean,
        is_oversteered_instrumentation_group18_number: boolean,
        is_oversteered_instrumentation_group1_comment: boolean,
        is_oversteered_instrumentation_group2_comment: boolean,
        is_oversteered_instrumentation_group3_comment: boolean,
        is_oversteered_instrumentation_group4_comment: boolean,
        is_oversteered_instrumentation_group5_comment: boolean,
        is_oversteered_instrumentation_group6_comment: boolean,
        is_oversteered_instrumentation_group7_comment: boolean,
        is_oversteered_instrumentation_group8_comment: boolean,
        is_oversteered_instrumentation_group9_comment: boolean,
        is_oversteered_instrumentation_group10_comment: boolean,
        is_oversteered_instrumentation_group11_comment: boolean,
        is_oversteered_instrumentation_group12_comment: boolean,
        is_oversteered_instrumentation_group13_comment: boolean,
        is_oversteered_instrumentation_group14_comment: boolean,
        is_oversteered_instrumentation_group15_comment: boolean,
        is_oversteered_instrumentation_group16_comment: boolean,
        is_oversteered_instrumentation_group17_comment: boolean,
        is_oversteered_instrumentation_group18_comment: boolean,
        instrumentation_group1_comment: string,
        instrumentation_group2_comment: string,
        instrumentation_group3_comment: string,
        instrumentation_group4_comment: string,
        instrumentation_group5_comment: string,
        instrumentation_group6_comment: string,
        instrumentation_group7_comment: string,
        instrumentation_group8_comment: string,
        instrumentation_group9_comment: string,
        instrumentation_group10_comment: string,
        instrumentation_group11_comment: string,
        instrumentation_group12_comment: string,
        instrumentation_group13_comment: string,
        instrumentation_group14_comment: string,
        instrumentation_group15_comment: string,
        instrumentation_group16_comment: string,
        instrumentation_group17_comment: string,
        instrumentation_group18_comment: string
    }

    interface _synced {
        is_synced_from_booking: boolean;
        is_sync_typ_A: boolean;
    }

    export interface EvProgram extends more.EntityAPIResultExtended, VoiceAttributes {
        c_composer_author: string,
        c_opus: string,
        duration_min: number,
        order: number,
        ref_opus: Opus
    }

    export interface EvProgramEvProject extends EvProgram, _synced {

    }

    export interface Opus extends more.EntityAPIResult, VoiceAttributes {
        ID: string,
        opus_titel: string
        duration_min: number
        gDisp_composer_author: string,
        gDisp_opus: string;
        instrumentation_code_1: string;
        opus_code: string;
    }


    export interface EvRoom {
        gDisp_1: string;
        is_with_public: boolean;
        ID: string;
        //venueID: string;
        //venueCode: string;
        //venueDescription: string;
        is_vacant_on_date: boolean;
    }

    export interface EvBooking_Extended extends EvBooking_Base{
        orchestraEmployments?: Array<OMtypes.orchestraEmployment>;
        evCategories?: Array<any>;
        existingFiles?: Array<any>;
        cast?: Array<any>;
        program?: Array<any>;
        service?: Array<any>;
        serviceCanceled: Array<any>;
        evTasks?: Array<any>;
        ref_audience_seating_layouts?: AudienceSeatingLayout;
        tickets_capacity: number;
        tickets_estimated: number;
        ref_booker?: { ID: string, gDisp_1?: string }
        sync_type_ev_program?: EvBookingSyncMode
        sync_type_ev_cast?: EvBookingSyncMode
    }



    export interface EvBookingBooker extends ApiEntity {
        gDisp_1: string;
    }

    export interface EvBookingPublicStatus extends ApiEntity {
        description: string;
    }
    export interface EvBookingIsPublicCheck extends ApiEntity {
        is_public: boolean;
    }


    export interface EvBooking_Base extends more.EntityAPIResult {
        date_end: Date;
        date_start: Date;
        ref_ev_project: EvProjectEntity_Base;
        is_full_time: boolean;
        gDisp_1: string;
        ev_booking_name: string;
        ref_ev_activity_status: EvActivityStatus;
        ref_ev_room: EvRoomEntity;
        ref_ev_booking_status: EvBookingStatus;
        is_public?: boolean;
    }

    export interface EvActivityStatus  {
        ID: string;
        description: string;
        coloration: more.EntityAPIColor;
    }
    export interface EvBookingStatus {
        ID: string;
        description: string;
        coloration: more.EntityAPIColor;
    }

    export interface EvProjectEntity_Base extends more.EntityAPIResultExtended {
        ev_project_name: string;
        gDisp_1: string;
        note: string;

        ref_artistic_period: {
            ID: string,
            description: string
        }
        ref_financial_period: {
            ID: string,
            description: string
        }
        ref_ev_project_type_2: {
            ID: string,
            description: string
        }
        ref_projectleader: {
            ID: string,
            gDisp_1: string
        }
        ref_promoter_1: EvProject_PromoterEntity,
        ref_promoter_2: EvProject_PromoterEntity,
        status_orchester_employment_registration?: number,
        $status_orchester_employment_registration?: string,
        offer_price_total?: number
    }
    
    export interface EvProject_PromoterEntity extends more.EntityAPIResult {
        ref_address: {
            ID: string,
            gDisp_1: string
        },
        ref_contact_person?: {
            ID: string,
            gDisp_1: string
        }
    }
    export interface EvProjectEntity_Extended extends EvProjectEntity_Base {
        bookings?: Array<VAM2types.EvBooking_Base>;
        plannedMusicians?: Array<OMtypes.plannedMusician>;
        existingFiles?: Array<any>;
        bookingFiles?: Array<any>;

        case_id?: string,
        crm_id?: string,
        ref_akquisitionperson?: {
            ID: string,
            gDisp_Name_Firstname: string
        }
        qty_audience?: number,
        ref_ev_project_status?: {
            ID: string,
            description: string
        }
        note_audience?: string
    }

    export interface AudienceSeatingLayout extends ApiEntity {
        code: string;
        description: string;
        gDisp_1: string;
        note: string;
        seating_capacity: number;
        is_default: boolean;
    }

    export interface DateSet {
        dateStart: Date,
        dateEnd: Date,
        isFulltime: boolean
    }

    export interface CopyFormData_EvBooking {
        evProjectID: string,
        sourceBookingID: string,
        targetDates: Array<DateSet> | any,
        copyEvProgram: boolean,
        copyEvCast: boolean,
        copyEvServiceBookings: boolean,
        copyEvTasks: boolean,
        activityStatusID: string,
        bookingStatusID: string,
        bookingTitle: string,
        evRoomID: string,
        overlappingCheckOnly?: boolean,
        ignoreOverlappings?: boolean
    }

    export interface CopyFormData_EvProject {
        artisticPeriodID: string,
        evProjectID: string,
        evProjectName: string,
        targetBookings: Array<string>,
        copyEvProgram: boolean,
        copyEvCast: boolean,
        copyEvTasks: boolean,
        copyEvServiceBookings: boolean,
        dateShift: number
    }

    export interface EvBookingCopyFormResponse<T> {
        result: "success" | string,
        message?: string,
        resultBookings?: Array<T>
    }
    export interface EvProjectCopyFormResponse<T> {
        result: "success" | string,
        message?: string,
        data: T
    }

    export interface EvBookingViewResponse {
        isHTMLdetailtext: boolean,
        content: string,
        entity: EvBooking_Base
    }

    export interface RoomBlockingFormData {
        datesToBlock: Array<DateSet> | any,
        evRoomID: string,
        listEvRoomID?: string[],
        evProjectID?: string
    }

    export interface RoomBlockingFormResponse {
        result: "success" | string,
        message?: string,
        data: any
    }
    export interface Entity_gDisp extends more.EntityAPIResultExtended {
        gDisp_1: string,
        gDisp_Firstname_Name?: string
    }
    export interface Entity_description extends more.EntityAPIResultExtended {
        description: string
        code?: string
    }
    export interface RelationType extends Entity_description {
        ref_relation_group: Entity_description
    }
    export interface InvolvedAddress extends more.EntityAPIResultExtended {
        ref_ev_project: Entity_gDisp,
        c_address_1: string,
        ref_address: Entity_gDisp,
        c_relation_type: string,
        ref_relation_type: RelationType,
        is_active: boolean,
        ref_relation_group: Entity_description,
        note: string,
        isNew?: boolean,
        is_to_publish?: boolean,
        publish_promoterdisplay?: string,
        publish_add_on_1?: string,
        ref_accounting_type?: Entity_description,
        ref_pricegroup?: Entity_description,
        ref_address_for_dispatch?: Entity_gDisp,
        ref_contact_person_dispatch?: Entity_gDisp,
        note_internal_use?: string,
        doc_txt_1?: string,
    }

    export interface InvolvedAddressEvBooking extends more.EntityAPIResultExtended {
        ref_ev_booking: EvBooking_Extended,
        c_address_1: string,
        ref_address: Entity_gDisp,
        c_relation_type: string,
        ref_relation_type: RelationType,
        is_active: boolean,
        ref_relation_group: Entity_description,
        note: string,
        note_internal_use: string,
        order: number
    }

    export interface DistributionChannels extends more.EntityAPIResultExtended {
        channelID: string,
        description: string
        ref_ev_distribution_channel: Entity_description
    }

    export interface CrudPrivilages {
        hasRead: boolean,
        hasEdit: boolean,
        hasCreate: boolean,
        hasDelete: boolean
    }
}
